import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth as oidcAuth } from 'react-oidc-context';
import { selectCurrentUser, setToken } from '../slice/authSlice';
import { persistor } from '../store';
import { api } from '../services/api';
import { isAuthorized } from '../utils/userUtils';

/**
 * Hook used to handle current user.
 * @returns {{currentUser: {user: *, isAuthorized: function(*, *): *}, logout: logout, resetOIDCToken: resetOIDCToken}}
 */
export const useAuth = () => {
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const authOIDC = oidcAuth();

  const currentUser = useMemo(
    () => ({
      user,
      isAuthorized: (functionality, operation) => isAuthorized(user, functionality, operation)
    }),
    [user]
  );

  /**
   * Sign the user out. Under OIDC, a top-level redirect to the provider is the only way to end the
   * provider session: a hidden iframe carries no session cookie, and Azure AD refuses to be framed.
   * On a public application no AuthProvider is mounted, so authOIDC is undefined and the local
   * sign-in page is the right destination.
   */
  const logout = () => {
    persistor.purge();
    dispatch(api.util.resetApiState());

    if (authOIDC?.isAuthenticated) {
      authOIDC.signoutRedirect();
    } else {
      window.location.assign(
        `${window.location.href.substring(0, window.location.href.indexOf('#') + 1)}/signIn`
      );
      window.location.reload();
    }
  };

  /**
   * Drop the local OIDC state without contacting the provider. Used on recovery paths, where the
   * token is unusable but the provider session may still be valid: the auto sign-in then renews it.
   */
  const resetOIDCToken = () => {
    if (!authOIDC) {
      return;
    }
    clearOIDCLocalStorage();
    authOIDC.revokeTokens();
    authOIDC.removeUser();
    dispatch(setToken(null));
  };

  /**
   * Remove OIDC keys from localStorage
   */
  function clearOIDCLocalStorage() {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('oidc')) {
        localStorage.removeItem(key);
      }
    });
  }

  return { currentUser, logout, resetOIDCToken };
};
